import { useEffect } from 'react'
import { Check, Globe } from 'lucide-react'
import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { Skeleton } from '../ui/skeleton'
import { Badge } from '../ui/badge'
import { useSiteStore } from '../../stores/siteStore'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'

export function SiteSelector() {
  const { sites, selectedSiteIds, setSites, toggleSite, setSelectedSite } = useSiteStore()

  const { data, isLoading } = useQuery({
    queryKey: ['sites'],
    queryFn: async () => {
      const response = await api.getSites()
      return response.data
    },
  })

  useEffect(() => {
    if (data) {
      setSites(data)
      // Auto-select first site if none selected
      if (selectedSiteIds.length === 0 && data.length > 0) {
        setSelectedSite(data[0].id)
      }
    }
  }, [data, selectedSiteIds.length, setSites, setSelectedSite])

  if (isLoading) {
    return <Skeleton className="h-10 w-48" />
  }

  const selectedCount = selectedSiteIds.length
  const allSelected = selectedCount === sites.length

  const getButtonText = () => {
    if (selectedCount === 0) return 'Select sites'
    if (allSelected) return 'All sites'
    if (selectedCount === 1) {
      const site = sites.find((s) => s.id === selectedSiteIds[0])
      return site?.domain || 'Select sites'
    }
    return `${selectedCount} sites`
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="justify-start">
          <Globe className="mr-2 h-4 w-4" />
          {getButtonText()}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Select Sites</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-[300px] overflow-y-auto">
          {sites.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No sites yet. Create one in Sites page.
            </div>
          ) : (
            sites.map((site) => {
              const isSelected = selectedSiteIds.includes(site.id)
              return (
                <button
                  key={site.id}
                  onClick={() => toggleSite(site.id)}
                  className="flex w-full items-center justify-between rounded-sm px-2 py-2 text-sm hover:bg-accent"
                >
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{site.domain}</span>
                    {site.name && (
                      <span className="text-xs text-muted-foreground">
                        {site.name}
                      </span>
                    )}
                  </div>
                  {isSelected && <Check className="h-4 w-4 text-primary" />}
                </button>
              )
            })
          )}
        </div>
        {sites.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2">
              <Badge variant="secondary" className="w-full justify-center">
                {selectedCount} / {sites.length} selected
              </Badge>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
